"use client";

import { useState, useRef, useEffect } from "react";
import Link from "next/link";
import { ChevronDown } from "lucide-react";
import { cn } from "../lib/utils";
import { useLanguage } from "../hooks/useLanguage";

export function PublicNavigation({
  className = "",
  isMobile = false,
  isFooter = false,
  menuItems = null,
}) {
  const [openDropdown, setOpenDropdown] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const buttonRefs = useRef({});
  const { language } = useLanguage();

  // Use only provided menu items from database - if no items, return empty array
  const navigation = menuItems && Array.isArray(menuItems) && menuItems.length > 0
    ? menuItems.filter(item => item.isActive !== false).sort((a, b) => (a.order || 0) - (b.order || 0))
    : [];
  
  // If no navigation items, don't render anything
  if (navigation.length === 0) {
    return null;
  }

  // Get label based on language
  const getLabel = (item) => {
    if (typeof item.label === 'string') return item.label;
    if (typeof item.name === 'string') return item.name;
    if (item.label && typeof item.label === 'object') {
      return item.label[language] || item.label.en || '';
    }
    if (item.name && typeof item.name === 'object') {
      return item.name[language] || item.name.en || '';
    }
    return '';
  };

  useEffect(() => {
    const updateDropdownPosition = () => {
      if (openDropdown && buttonRefs.current[openDropdown]) {
        const rect = buttonRefs.current[openDropdown].getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 8,
          left: rect.left,
        });
      }
    };

    updateDropdownPosition();

    const handleScroll = () => {
      updateDropdownPosition();
    };

    const handleClickOutside = (event) => {
      if (
        openDropdown &&
        !event.target.closest(".public-navigation") &&
        !event.target.closest(".footer-navigation")
      ) {
        setOpenDropdown(null);
      }
    };

    if (openDropdown) {
      window.addEventListener("scroll", handleScroll);
      window.addEventListener("resize", handleScroll);
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
      document.removeEventListener("click", handleClickOutside);
    };
  }, [openDropdown]);

  // Mobile layout
  if (isMobile) {
    return (
      <nav className={`flex flex-col space-y-2 sm:space-y-3 ${className}`}>
        {navigation.map((menu) => {
          const menuLabel = getLabel(menu);
          
          // Handle submenu items from database  
          if (menu.submenu && menu.submenu.length > 0) {
            const activeSubmenus = menu.submenu.filter(sub => sub.isActive !== false);
            
            return (
              <div key={menu.id || menuLabel} className="py-1 sm:py-2 px-2">
                <details className="group">
                  <summary className="transition-all duration-300 cursor-pointer flex items-center justify-between font-poppins text-sm sm:text-base" style={{ color: 'var(--color-card-foreground)' }}>
                    <span className="truncate language-transition flex items-center gap-1">
                      {menuLabel}
                    </span>
                    <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5 group-open:rotate-180 transition-transform flex-shrink-0 ml-2" />
                  </summary>
                  <div className="mt-2 ml-3 sm:ml-4 space-y-1 sm:space-y-2">
                    {activeSubmenus.map((subItem, index) => (
                      <Link
                        key={index}
                        href={subItem.url}
                        className="flex py-1 sm:py-2 text-sm sm:text-base truncate items-center gap-1 rounded px-2 transition-all duration-200"
                        style={{ color: 'var(--color-muted-foreground)' }}
                        onMouseOver={(e) => e.currentTarget.style.color = 'var(--color-primary)'}
                        onMouseOut={(e) => e.currentTarget.style.color = 'var(--color-muted-foreground)'}
                      >
                        {getLabel(subItem)}
                      </Link>
                    ))}
                  </div>
                </details>
              </div>
            );
          } else if (menu.children && menu.children.length > 0) {
            return (
              <div key={menu.id || menuLabel} className="py-1 sm:py-2 px-2">
                <details className="group">
                  <summary className="transition-all duration-300 cursor-pointer flex items-center justify-between font-poppins text-sm sm:text-base" style={{ color: 'var(--color-card-foreground)' }}>
                    <span className="truncate language-transition flex items-center gap-1">
                      {menuLabel}
                    </span>
                    <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5 group-open:rotate-180 transition-transform flex-shrink-0 ml-2" />
                  </summary>
                  <div className="mt-2 ml-3 sm:ml-4 space-y-1 sm:space-y-2">
                    {menu.children.filter(child => child.isActive !== false).map((child) => (
                      <Link
                        key={child.id || getLabel(child)}
                        href={child.href || child.url}
                        className="flex py-1 sm:py-2 text-sm sm:text-base truncate items-center gap-1 rounded px-2 transition-all duration-200"
                        style={{ color: 'var(--color-muted-foreground)' }}
                        onMouseOver={(e) => e.currentTarget.style.color = 'var(--color-primary)'}
                        onMouseOut={(e) => e.currentTarget.style.color = 'var(--color-muted-foreground)'}
                      >
                        {getLabel(child)}
                      </Link>
                    ))}
                  </div>
                </details>
              </div>
            );
          } else {
            return (
              <Link
                key={menu.id || menuLabel}
                href={menu.href || menu.url}
                className="transition-all duration-300 py-2 sm:py-3 px-2 sm:px-3 rounded-md font-poppins text-sm sm:text-base truncate language-transition flex items-center gap-1"
                style={{ color: 'var(--color-card-foreground)' }}
                onMouseOver={(e) => e.currentTarget.style.color = 'var(--color-primary)'}
                onMouseOut={(e) => e.currentTarget.style.color = 'var(--color-card-foreground)'}
              >
                {menuLabel}
              </Link>
            );
          }
        })}
      </nav>
    );
  }

  // Footer layout with custom dropdown
  if (isFooter) {
    return (
      <div className="footer-navigation relative">
        <div
          className={`flex flex-wrap justify-center md:justify-start gap-2 sm:gap-4 ${className}`}
        >
          {navigation.map((menu) => {
            const menuLabel = getLabel(menu);
            
            // Handle submenu items from database
            if (menu.submenu && menu.submenu.length > 0) {
              const activeSubmenus = menu.submenu.filter(sub => sub.isActive !== false);
              
              return (
                <div key={menu.id || menuLabel} className="relative">
                  <button
                    onClick={() =>
                      setOpenDropdown(
                        openDropdown === menuLabel ? null : menuLabel
                      )
                    }
                    className="hover:underline transition-colors whitespace-nowrap flex items-center gap-1"
                    style={{ color: 'var(--color-muted-foreground)' }}
                    onMouseOver={(e) => e.currentTarget.style.color = 'var(--color-primary)'}
                    onMouseOut={(e) => e.currentTarget.style.color = 'var(--color-muted-foreground)'}
                  >
                    {menuLabel}
                    <ChevronDown
                      className={`h-3 w-3 ml-1 transition-transform ${
                        openDropdown === menuLabel ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {openDropdown === menuLabel && (
                    <div className="absolute bottom-full left-0 mb-2 min-w-[200px] shadow-lg overflow-hidden z-[9999]" style={{ backgroundColor: 'var(--color-card)', borderWidth: '1px', borderStyle: 'solid', borderColor: 'var(--color-border)', borderTop: '3px solid var(--color-primary)' }}>
                      <div className="px-3 py-2" style={{ backgroundColor: 'var(--color-muted)', borderBottomWidth: '1px', borderBottomStyle: 'solid', borderBottomColor: 'var(--color-border)' }}>
                        <span className="text-xs font-medium" style={{ color: 'var(--color-foreground)' }}>
                          {menuLabel}
                        </span>
                      </div>
                      <div className="py-1">
                        {activeSubmenus.map((subItem, index) => (
                          <Link
                            key={index}
                            href={subItem.url}
                            className="flex items-center gap-2 px-3 py-2 text-xs transition-colors"
                            style={{ color: 'var(--color-muted-foreground)' }}
                            onMouseOver={(e) => { e.currentTarget.style.color = 'var(--color-primary)'; }}
                            onMouseOut={(e) => { e.currentTarget.style.color = 'var(--color-muted-foreground)'; }}
                          >
                            <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'var(--color-border)' }}></div>
                            <span>{getLabel(subItem)}</span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            } else if (menu.children && menu.children.length > 0) {
              return (
                <div key={menu.id || menuLabel} className="relative">
                  <button
                    onClick={() =>
                      setOpenDropdown(
                        openDropdown === menuLabel ? null : menuLabel
                      )
                    }
                    className="hover:underline transition-colors whitespace-nowrap flex items-center gap-1"
                    style={{ color: 'var(--color-muted-foreground)' }}
                    onMouseOver={(e) => e.currentTarget.style.color = 'var(--color-primary)'}
                    onMouseOut={(e) => e.currentTarget.style.color = 'var(--color-muted-foreground)'}
                  >
                    {menuLabel}
                    <ChevronDown
                      className={`h-3 w-3 ml-1 transition-transform ${
                        openDropdown === menuLabel ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {openDropdown === menuLabel && (
                    <div className="absolute bottom-full left-0 mb-2 min-w-[200px] rounded-sm shadow-lg overflow-hidden z-[9999]" style={{ backgroundColor: 'var(--color-card)', borderWidth: '1px', borderStyle: 'solid', borderColor: 'var(--color-border)' }}>
                      <div className="px-3 py-2" style={{ backgroundColor: 'var(--color-muted)', borderBottomWidth: '1px', borderBottomStyle: 'solid', borderBottomColor: 'var(--color-border)' }}>
                        <span className="text-xs font-medium" style={{ color: 'var(--color-foreground)' }}>
                          {menuLabel}
                        </span>
                      </div>
                      <div className="py-1">
                        {menu.children.filter(child => child.isActive !== false).map((child) => (
                          <Link
                            key={child.id || getLabel(child)}
                            href={child.href || child.url}
                            className="flex items-center gap-2 px-3 py-2 text-xs transition-colors"
                            style={{ color: 'var(--color-muted-foreground)' }}
                            onMouseOver={(e) => { e.currentTarget.style.color = 'var(--color-primary)'; }}
                            onMouseOut={(e) => { e.currentTarget.style.color = 'var(--color-muted-foreground)'; }}
                          >
                            <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'var(--color-border)' }}></div>
                            <span>{getLabel(child)}</span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            } else {
              return (
                <Link
                  key={menu.id || menuLabel}
                  href={menu.href || menu.url}
                  className="hover:underline transition-colors whitespace-nowrap flex items-center gap-1"
                  style={{ color: 'var(--color-muted-foreground)' }}
                  onMouseOver={(e) => e.currentTarget.style.color = 'var(--color-primary)'}
                  onMouseOut={(e) => e.currentTarget.style.color = 'var(--color-muted-foreground)'}
                >
                  {menuLabel}
                </Link>
              );
            }
          })}
        </div>
      </div>
    );
  }

  // Desktop header layout with custom dropdown
  return (
    <>
      <div className="public-navigation relative">
        <div
          className={`flex items-center gap-0.5 sm:gap-1 md:gap-1.5 xl:gap-2 font-poppins flex-shrink-0 ${className}`}
        >
          {navigation.map((menu) => {
            const menuLabel = getLabel(menu);
            
            // Handle submenu items from database
            if (menu.submenu && menu.submenu.length > 0) {
              const activeSubmenus = menu.submenu.filter(sub => sub.isActive !== false);
              
              return (
                <div key={menu.id || menuLabel} className="relative">
                  <button
                    ref={(el) => (buttonRefs.current[menuLabel] = el)}
                    onClick={() =>
                      setOpenDropdown(
                        openDropdown === menuLabel ? null : menuLabel
                      )
                    }
                    className="transition-all duration-300 px-1 sm:px-1 md:px-1.5 xl:px-2 py-0.5 text-[11px] sm:text-xs md:text-sm whitespace-nowrap flex items-center gap-1 rounded"
                    style={{ color: 'var(--color-card-foreground)' }}
                    onMouseOver={(e) => { e.currentTarget.style.color = 'var(--color-primary)'; e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'; }}
                    onMouseOut={(e) => { e.currentTarget.style.color = 'var(--color-card-foreground)'; e.currentTarget.style.backgroundColor = 'transparent'; }}
                  >
                    {menuLabel}
                    <ChevronDown
                      className={`h-3 w-3 sm:h-3.5 sm:w-3.5 ml-1 transition-transform ${
                        openDropdown === menuLabel ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                </div>
              );
            } else if (menu.children && menu.children.length > 0) {
              return (
                <div key={menu.id || menuLabel} className="relative">
                  <button
                    ref={(el) => (buttonRefs.current[menuLabel] = el)}
                    onClick={() =>
                      setOpenDropdown(
                        openDropdown === menuLabel ? null : menuLabel
                      )
                    }
                    className="transition-all duration-300 px-1 sm:px-1 md:px-1.5 xl:px-2 py-0.5 text-[11px] sm:text-xs md:text-sm whitespace-nowrap flex items-center gap-1 rounded"
                    style={{ color: 'var(--color-card-foreground)' }}
                    onMouseOver={(e) => { e.currentTarget.style.color = 'var(--color-primary)'; e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'; }}
                    onMouseOut={(e) => { e.currentTarget.style.color = 'var(--color-card-foreground)'; e.currentTarget.style.backgroundColor = 'transparent'; }}
                  >
                    {menuLabel}
                    <ChevronDown
                      className={`h-3 w-3 sm:h-3.5 sm:w-3.5 ml-1 transition-transform ${
                        openDropdown === menuLabel ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                </div>
              );
            } else {
              return (
                <Link
                  key={menu.id || menuLabel}
                  href={menu.href || menu.url}
                  className="transition-all duration-300 px-1 sm:px-1 md:px-1.5 xl:px-2 py-0.5 text-[11px] sm:text-xs md:text-sm whitespace-nowrap flex items-center gap-1"
                  style={{ color: 'var(--color-card-foreground)' }}
                  onMouseOver={(e) => { e.currentTarget.style.color = 'var(--color-primary)'; }}
                  onMouseOut={(e) => { e.currentTarget.style.color = 'var(--color-card-foreground)'; }}
                >
                  {menuLabel}
                </Link>
              );
            }
          })}
        </div>
      </div>

      {/* Dropdown rendered outside - fixed positioning with rectangular design */}
      {openDropdown && (
        <div
          className="fixed min-w-[180px] sm:min-w-[200px] md:min-w-[220px] shadow-xl py-2 z-[99999]"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            backgroundColor: 'var(--color-card)',
            border: `1px solid var(--color-border)`,
            borderTop: `3px solid var(--color-primary)`,
          }}
        >
          {navigation.map((menu) => {
            const menuLabel = getLabel(menu);
            if (menuLabel === openDropdown) {
              // Handle submenu from database
              if (menu.submenu && menu.submenu.length > 0) {
                const activeSubmenus = menu.submenu.filter(sub => sub.isActive !== false);
                return activeSubmenus.map((subItem, index) => (
                  <Link
                    key={index}
                    href={subItem.url}
                    className="block px-4 py-2 text-sm transition-colors"
                    style={{ color: 'var(--color-muted-foreground)' }}
                    onMouseOver={(e) => { e.currentTarget.style.color = 'var(--color-primary)'; }}
                    onMouseOut={(e) => { e.currentTarget.style.color = 'var(--color-muted-foreground)'; }}
                    onClick={() => setOpenDropdown(null)}
                  >
                    {getLabel(subItem)}
                  </Link>
                ));
              } else if (menu.children && menu.children.length > 0) {
                return menu.children.map((child, index) => (
                  <Link
                    key={index}
                    href={child.href || child.url}
                    className="block px-4 py-2 text-sm transition-colors"
                    style={{ color: 'var(--color-muted-foreground)' }}
                    onMouseOver={(e) => { e.currentTarget.style.color = 'var(--color-primary)'; }}
                    onMouseOut={(e) => { e.currentTarget.style.color = 'var(--color-muted-foreground)'; }}
                    onClick={() => setOpenDropdown(null)}
                  >
                    {getLabel(child)}
                  </Link>
                ));
              }
            }
            return null;
          })}
        </div>
      )}
    </>
  );
}

export default PublicNavigation;
